import React from 'react';
import { Box, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ink } from '../../theme/tokens';

export interface ValidationProblem {
    severity?: string;
    field?: string;
    message?: string;
}

/**
 * Validation output from POST /ontology/validate.
 *
 * The severity split is load-bearing and must survive into the UI:
 *
 *   errors   — the document would break resolution. Block the write.
 *   warnings — the document is valid but breaches a regulatory constraint or cites
 *              something that does not resolve. SHOW IT, never block. A bank may always
 *              be stricter than statute and never looser, but refusing the write would
 *              leave it with no configured value at all — which is worse than a value
 *              someone has been told about. A threshold looser than statute is exactly
 *              the finding an examiner asks about, so it is rendered prominently rather
 *              than tucked away.
 */
const ValidationMessages: React.FC<{
    errors?: ValidationProblem[];
    warnings?: ValidationProblem[];
}> = ({ errors = [], warnings = [] }) => {
    if (errors.length === 0 && warnings.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {errors.map((problem, index) => (
                <Box
                    key={`error-${index}`}
                    sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1,
                        p: 1.25, borderRadius: '6px',
                        backgroundColor: 'rgba(248, 113, 113, 0.08)',
                        border: '1px solid rgba(248, 113, 113, 0.35)',
                    }}
                >
                    <ErrorOutlineIcon sx={{ fontSize: 18, color: ink('#F87171'), mt: '1px', flexShrink: 0 }} />
                    <Typography component="div" sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), lineHeight: 1.6 }}>
                        {problem.field && (
                            <Typography
                                component="span"
                                sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', mr: 0.75 }}
                            >
                                {problem.field}
                            </Typography>
                        )}
                        {problem.message}
                    </Typography>
                </Box>
            ))}

            {warnings.map((problem, index) => (
                <Box
                    key={`warning-${index}`}
                    sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1,
                        p: 1.25, borderRadius: '6px',
                        backgroundColor: 'rgba(251, 191, 36, 0.08)',
                        border: '1px solid rgba(251, 191, 36, 0.35)',
                    }}
                >
                    <WarningAmberIcon sx={{ fontSize: 18, color: ink('#FBBF24'), mt: '1px', flexShrink: 0 }} />
                    <Typography component="div" sx={{ fontSize: '0.8125rem', color: ink('#FCD34D'), lineHeight: 1.6 }}>
                        {problem.field && (
                            <Typography
                                component="span"
                                sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', mr: 0.75 }}
                            >
                                {problem.field}
                            </Typography>
                        )}
                        {problem.message}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

export default ValidationMessages;
